import { useState, useEffect, useMemo } from 'react';
import { useSubscription } from './useSubscription';
import { FeatureName, FeatureLevel } from '../types/features';

interface PlanFeature {
  name: FeatureName;
  level: FeatureLevel;
  available: boolean;
  displayName: string;
}

interface UsePlanFeaturesResult {
  features: PlanFeature[];
  hasFeature: (featureName: FeatureName) => boolean;
  getFeatureLevel: (featureName: FeatureName) => FeatureLevel | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook pour accéder aux features du plan actuel de l'utilisateur
 *
 * Usage:
 *   const { features, hasFeature, getFeatureLevel } = usePlanFeatures();
 *
 *   if (hasFeature('export_bicep')) {
 *     // L'utilisateur peut exporter en Bicep
 *   }
 *
 *   const finopsLevel = getFeatureLevel('finops_advanced');
 *   // Returns: 'standard', 'advanced', null, etc.
 */
export function usePlanFeatures(): UsePlanFeaturesResult {
  const { subscription, loading, error } = useSubscription();
  const [features, setFeatures] = useState<PlanFeature[]>([]);

  // Mapper les features depuis la subscription
  useEffect(() => {
    if (!subscription) {
      setFeatures([]);
      return;
    }

    // Pour l'instant, on parse les features depuis l'API
    // TODO: Améliorer l'API pour retourner un objet features structuré
    const parsedFeatures: PlanFeature[] = [];

    // Mapping basique pour démo
    // Dans une implémentation réelle, ces features viendraient de l'API
    const planFeaturesMap: Record<string, Record<string, FeatureLevel>> = {
      free: {
        multicloud: true,
        export_terraform: true,
        export_bicep: false,
        cost_estimation: 'basic',
        monitoring: 'basic',
        finops_advanced: false,
        sso: false,
        audit_logs: false,
        api_access: false
      },
      standard: {
        multicloud: true,
        export_terraform: true,
        export_bicep: true,
        cost_estimation: 'standard',
        monitoring: 'standard',
        finops_advanced: 'standard',
        sso: false,
        audit_logs: 'retention_30',
        api_access: 'limited'
      },
      entreprise: {
        multicloud: true,
        export_terraform: true,
        export_bicep: true,
        cost_estimation: 'advanced',
        monitoring: 'advanced',
        finops_advanced: 'advanced',
        sso: true,
        audit_logs: 'unlimited',
        api_access: 'unlimited',
        dedicated_support: true,
        priority_support: true
      },
      business: {
        multicloud: true,
        export_terraform: true,
        export_bicep: true,
        cost_estimation: 'advanced',
        monitoring: 'advanced',
        finops_advanced: 'advanced',
        sso: true,
        audit_logs: 'unlimited',
        api_access: 'unlimited',
        white_label: true,
        dedicated_support: true,
        priority_support: true,
        byoo_azure_devops: true,
        multi_tenant_clients: true,
        sla: true,
        custom_integrations: true
      }
    };

    const planFeatures = planFeaturesMap[subscription.plan] || {};

    Object.entries(planFeatures).forEach(([name, level]) => {
      if (level) {
        parsedFeatures.push({
          name: name as FeatureName,
          level: level as FeatureLevel,
          available: true,
          displayName: getFeatureDisplayName(name as FeatureName)
        });
      }
    });

    setFeatures(parsedFeatures);
  }, [subscription]);

  const hasFeature = useMemo(() => {
    return (featureName: FeatureName): boolean => {
      return features.some(f => f.name === featureName && f.available);
    };
  }, [features]);

  const getFeatureLevel = useMemo(() => {
    return (featureName: FeatureName): FeatureLevel | null => {
      const feature = features.find(f => f.name === featureName);
      return feature ? feature.level : null;
    };
  }, [features]);

  return {
    features,
    hasFeature,
    getFeatureLevel,
    loading,
    error
  };
}

/**
 * Obtenir le nom d'affichage d'une feature
 */
function getFeatureDisplayName(featureName: FeatureName): string {
  const displayNames: Record<FeatureName, string> = {
    multicloud: 'Multi-cloud',
    export_terraform: 'Export Terraform',
    export_bicep: 'Export Bicep',
    cost_estimation: 'Estimation de coûts',
    monitoring: 'Monitoring',
    finops_advanced: 'FinOps',
    sso: 'SSO (SAML/OAuth)',
    audit_logs: 'Logs d\'audit',
    api_access: 'Accès API',
    white_label: 'Marque blanche',
    dedicated_support: 'Support dédié',
    priority_support: 'Support prioritaire',
    byoo_azure_devops: 'BYOO Azure DevOps',
    multi_tenant_clients: 'Multi-tenant',
    sla: 'SLA 99.9%',
    custom_integrations: 'Intégrations custom'
  };

  return displayNames[featureName] || featureName;
}

export default usePlanFeatures;
